<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell *ngIf="!isLoading && company">
    <app-page-detail-hero
        eyebrow="Company Profile"
        [title]="company.name"
        [subtitle]="company.address || company.emailAddress || 'Company profile'"
        icon="pi pi-building">
        <div tags>
            <p-tag [value]="'ID ' + company.id" severity="info"></p-tag>
            <p-tag *ngIf="company.crNumber" [value]="'CR ' + company.crNumber" severity="success"></p-tag>
            <p-tag [value]="company.enableEnterpriseFeatures ? 'Enterprise Enabled' : 'Standard'" [severity]="company.enableEnterpriseFeatures ? 'success' : 'secondary'"></p-tag>
        </div>
        <div actions>
            <p-button *ngIf="isCompanyScope" icon="pi pi-pencil" label="Edit Company" (click)="editCompany()"></p-button>
        </div>
    </app-page-detail-hero>

    <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

    <app-page-data-panel title="Company Overview" subtitle="Registration, contact details, and operating context.">
        <div class="company-overview">
            <div class="brand-block">
                <div class="brand-logo">
                    <p-image [src]="company.logo || 'https://cdn-icons-png.flaticon.com/512/4299/4299051.png'"
                        alt="Company Logo" imageClass="company-logo-image"></p-image>
                    <ng-container *ngIf="isCompanyScope">
                        <input #companyLogoInput type="file" accept="image/*" hidden
                            (change)="uploadCompanyLogo($event)" />
                        <p-button type="button" [icon]="company.logo ? 'pi pi-refresh' : 'pi pi-upload'"
                            [label]="company.logo ? 'Replace Logo' : 'Upload Logo'" size="small"
                            [loading]="isLogoUploadInProgress" [disabled]="isLogoUploadInProgress"
                            (onClick)="companyLogoInput.click()" styleClass="logo-upload-button"></p-button>
                    </ng-container>
                </div>
                <div class="brand-copy">
                    <h3>{{ company.name }}</h3>
                    <p *ngIf="company.nameLocal" dir="rtl">{{ company.nameLocal }}</p>
                    <a *ngIf="company.websiteURL" [href]="company.websiteURL" target="_blank" rel="noopener">
                        {{ company.websiteURL }}
                    </a>
                    <p *ngIf="!company.websiteURL">No website captured</p>
                    <p *ngIf="isLogoUploadInProgress">Uploading logo {{ logoUploadProgress }}%</p>
                </div>
            </div>
            <div class="detail-list">
                <div class="detail-row">
                    <span>CR Number</span>
                    <strong>{{ company.crNumber || 'N/A' }}</strong>
                </div>
                <div class="detail-row">
                    <span>VAT Number</span>
                    <strong>{{ company.taxRegNumber || 'N/A' }}</strong>
                </div>
                <div class="detail-row">
                    <span>Email</span>
                    <strong>{{ company.emailAddress || 'N/A' }}</strong>
                </div>
                <div class="detail-row">
                    <span>Phone</span>
                    <strong>{{ company.phoneNumber || 'N/A' }}</strong>
                </div>
                <div class="detail-row">
                    <span>Address</span>
                    <a *ngIf="company.location" target="_blank"
                        href="https://maps.google.com?q={{ company.location.latitude }},{{ company.location.longitude }}">
                        {{ company.address || 'Open map' }}
                    </a>
                    <strong *ngIf="!company.location">{{ company.address || 'N/A' }}</strong>
                </div>
                <div class="detail-row">
                    <span>Arabic Address</span>
                    <strong dir="rtl">{{ company.addressLocal || 'N/A' }}</strong>
                </div>
            </div>
        </div>
    </app-page-data-panel>

    <app-page-data-panel title="Company Workspace" subtitle="Employees, bank accounts, and company settings.">
        <p-tabs [value]="activeTabIndex" (valueChange)="onTabChange({ index: $event })">
            <p-tablist>
                <p-tab [value]="0"><span class="tab-label"><i class="pi pi-users"></i> Employees</span></p-tab>
                <p-tab [value]="1"><span class="tab-label"><i class="pi pi-wallet"></i> Bank Accounts</span></p-tab>
                <p-tab [value]="2"><span class="tab-label"><i class="pi pi-file"></i> Files</span></p-tab>
                <p-tab [value]="3"><span class="tab-label"><i class="pi pi-cog"></i> Settings</span></p-tab>
            </p-tablist>
            <p-tabpanels>
                <p-tabpanel [value]="0">
                    <div class="mb-3 p-3 surface-card border-1 surface-border border-round flex align-items-center justify-content-between flex-wrap gap-2">
                        <div>
                            <div class="font-bold text-900 mb-1"><i class="pi pi-users text-primary mr-2"></i> Dedicated Users & Roles Page</div>
                            <span class="text-color-secondary text-sm">Employees, custom roles, and permission composition are managed in the dedicated <strong>Users & Roles</strong> hub.</span>
                        </div>
                        <a pButton routerLink="/company/users-and-roles" label="Open Users & Roles" icon="pi pi-arrow-right" iconPos="right" size="small"></a>
                    </div>
                    <div class="table-toolbar">
                        <span class="search-field">
                            <i class="pi pi-search"></i>
                            <input pInputText [(ngModel)]="employeeSearch" placeholder="Search employees" />
                        </span>
                        <p-button *ngIf="isCompanyScope" type="button" label="New Employee" icon="pi pi-plus" (click)="createNewCustomer()"></p-button>
                    </div>
                    <p-table [value]="visibleCustomers" dataKey="id" styleClass="p-datatable-sm"
                        [tableStyle]="{ 'min-width': '62rem' }">
                        <ng-template pTemplate="header">
                            <tr>
                                <th>ID</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Phone Number</th>
                                <th>Roles</th>
                                <th class="text-right">Actions</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-customer>
                            <tr>
                                <td>{{ customer.id }}</td>
                                <td>
                                    <div class="identity-cell">
                                        <p-avatar [image]="customer.imageUrl || 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png'" shape="circle"></p-avatar>
                                        <strong>{{ customer.name }}</strong>
                                    </div>
                                </td>
                                <td>{{ customer.emailAddress?.includes('@abcxyz.com') ? '' : customer.emailAddress }}</td>
                                <td>{{ customer.phoneNumber || 'N/A' }}</td>
                                <td>
                                    <div class="chip-list">
                                        <p-tag *ngFor="let role of customer.roles" [value]="role.roleName"></p-tag>
                                    </div>
                                </td>
                                <td class="text-right">
                                    <button pButton pTooltip="Send Email" type="button" icon="pi pi-envelope"
                                        (click)="sendEmail(customer.emailAddress)" [disabled]="customer.emailAddress?.includes('@abcxyz.com')"
                                        class="p-button-rounded p-button-text p-button-secondary"></button>
                                    <button pButton pTooltip="Call" type="button" icon="pi pi-phone"
                                        (click)="makePhoneCall(customer.phoneNumber)" class="p-button-rounded p-button-text p-button-success"></button>
                                    <button *ngIf="isCompanyScope" pButton pTooltip="Manage Roles" type="button" icon="pi pi-users"
                                        (click)="manageRoles(customer)" class="p-button-rounded p-button-text p-button-help"></button>
                                    <button *ngIf="isCompanyScope" pButton pTooltip="Edit" type="button" icon="pi pi-pencil"
                                        (click)="editCustomer(customer)" class="p-button-rounded p-button-text p-button-secondary"></button>
                                    <button *ngIf="isCompanyScope" pButton pTooltip="Delete" type="button" icon="pi pi-trash"
                                        (click)="deleteCustomer(customer)" class="p-button-rounded p-button-text p-button-danger"></button>
                                </td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr><td colspan="6" class="text-center p-4">No employees found.</td></tr>
                        </ng-template>
                    </p-table>
                </p-tabpanel>

                <p-tabpanel [value]="1">
                    <div class="table-toolbar">
                        <span class="search-field">
                            <i class="pi pi-search"></i>
                            <input pInputText [(ngModel)]="bankAccountSearch" placeholder="Search bank accounts" />
                        </span>
                        <p-button *ngIf="isCompanyScope" type="button" label="New Bank Account" icon="pi pi-plus" (click)="openBankAccountDialog()"></p-button>
                    </div>
                    <p-table [value]="visibleBankAccounts" dataKey="id" styleClass="p-datatable-sm"
                        [tableStyle]="{ 'min-width': '62rem' }">
                        <ng-template pTemplate="header">
                            <tr>
                                <th>Bank Name</th>
                                <th>Account Name</th>
                                <th>Account Number</th>
                                <th>IBAN</th>
                                <th>Currency</th>
                                <th class="text-right">Actions</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-account>
                            <tr>
                                <td>
                                    <div class="identity-cell">
                                        <p-image [src]="account.bank?.logoUrl || 'https://cdn-icons-png.flaticon.com/512/888/888861.png'"
                                            alt="Bank Logo" height="24" imageClass="border-round"></p-image>
                                        <strong>{{ account.bank?.name || 'Bank' }}</strong>
                                    </div>
                                </td>
                                <td>{{ account.accountName }}</td>
                                <td>{{ account.accountNumber }}</td>
                                <td>{{ account.iban }}</td>
                                <td><p-tag [value]="account.currencyCode || 'N/A'"></p-tag></td>
                                <td class="text-right">
                                    <button *ngIf="isCompanyScope" pButton pTooltip="Edit" type="button" icon="pi pi-pencil"
                                        class="p-button-rounded p-button-text p-button-secondary" (click)="openBankAccountDialog(account)"></button>
                                    <button *ngIf="isCompanyScope" pButton pTooltip="Delete" type="button" icon="pi pi-trash"
                                        class="p-button-rounded p-button-text p-button-danger" (click)="deleteBankAccount(account)"></button>
                                </td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr><td colspan="6" class="text-center p-4">No bank accounts found.</td></tr>
                        </ng-template>
                    </p-table>
                </p-tabpanel>

                <p-tabpanel [value]="2">
                    <div class="table-toolbar">
                        <span class="files-summary">{{ companyFiles.length }} company files</span>
                        <p-button *ngIf="canManageCompanyFiles" type="button" label="Add File" icon="pi pi-plus" (click)="openCompanyFileDialog()"></p-button>
                    </div>
                    <p-table [value]="companyFiles" dataKey="id" styleClass="p-datatable-sm"
                        [tableStyle]="{ 'min-width': '62rem' }">
                        <ng-template pTemplate="header">
                            <tr>
                                <th>File Type</th>
                                <th>File Name</th>
                                <th>Uploaded By</th>
                                <th>Uploaded At</th>
                                <th class="text-right">Actions</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-file>
                            <tr>
                                <td><p-tag [value]="fileCategoryLabel(file.category)"></p-tag></td>
                                <td>{{ file.fileName }}</td>
                                <td>{{ file.uploadedByCustomerName || 'N/A' }}</td>
                                <td>{{ file.uploadedAt | date:'medium' }}</td>
                                <td class="text-right">
                                    <a pButton pTooltip="Open File" type="button" icon="pi pi-external-link"
                                        [href]="file.fileUrl" target="_blank" rel="noopener"
                                        class="p-button-rounded p-button-text p-button-secondary"></a>
                                    <button *ngIf="canManageCompanyFiles" pButton pTooltip="Delete" type="button" icon="pi pi-trash"
                                        (click)="deleteCompanyFile(file)" class="p-button-rounded p-button-text p-button-danger"></button>
                                </td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr><td colspan="5" class="text-center p-4">No company files uploaded.</td></tr>
                        </ng-template>
                    </p-table>
                </p-tabpanel>

                <p-tabpanel [value]="3">
                    <div class="settings-grid">
                        <div class="setting-row">
                            <span>Enterprise Features</span>
                            <p-tag [value]="company.enableEnterpriseFeatures ? 'Enabled' : 'Disabled'"
                                [severity]="company.enableEnterpriseFeatures ? 'success' : 'secondary'"></p-tag>
                        </div>
                        <div class="setting-row">
                            <span>Default Contact ID</span>
                            <strong>{{ company.defaultContactId || 'N/A' }}</strong>
                        </div>
                        <div class="setting-row">
                            <span>Transportation Agency ID</span>
                            <strong>{{ company.transportationAgencyId || 'N/A' }}</strong>
                        </div>
                        <div class="setting-row">
                            <span>Freight Forwarder ID</span>
                            <strong>{{ company.freightForwarderId || 'N/A' }}</strong>
                        </div>
                        <div class="setting-row">
                            <span>Active Company Group</span>
                            <div class="setting-actions">
                                <p-tag *ngIf="activeGroupAccess" [value]="activeGroupAccess.companyGroupName" severity="info"></p-tag>
                                <strong *ngIf="!activeGroupAccess">No active company group</strong>
                                <p-button type="button" label="Open Company Groups" icon="pi pi-sitemap"
                                    severity="secondary" size="small" [outlined]="true"
                                    (click)="openCompanyGroupsPage()"></p-button>
                            </div>
                        </div>
                        <div class="setting-row" *ngIf="activeGroupAccess">
                            <span>Group Company Count</span>
                            <strong>{{ groupCompanyCount }}</strong>
                        </div>
                        <div class="setting-row" *ngIf="activeGroupAccess && groupMemberCompanies.length">
                            <span>Member Companies</span>
                            <div class="chip-list">
                                <p-tag *ngFor="let memberCompany of groupMemberCompanies" [value]="memberCompany.name"></p-tag>
                            </div>
                        </div>
                        <div class="setting-row" *ngIf="company.enableEnterpriseFeatures">
                            <span>Bayan Naql</span>
                            <div class="setting-actions">
                                <p-tag *ngIf="bayanStatus?.onboarded" value="Connected" severity="success"></p-tag>
                                <p-tag *ngIf="!bayanStatus?.onboarded && bayanStatus" value="Not Connected" severity="secondary"></p-tag>
                                <p-button *ngIf="bayanStatus?.onboarded && isCompanyScope" type="button" label="Remove" icon="pi pi-trash"
                                    severity="danger" size="small" [loading]="bayanLoading" (click)="deboardBayan()"></p-button>
                                <p-button *ngIf="!bayanStatus?.onboarded && isCompanyScope" type="button" label="Connect Naql" icon="pi pi-link"
                                    size="small" [loading]="bayanLoading" (click)="openBayanDialog()"></p-button>
                            </div>
                        </div>
                        <div class="setting-row" *ngIf="bayanStatus?.onboarded && bayanStatus?.clientId">
                            <span>Naql Client ID</span>
                            <strong>{{ bayanStatus?.clientId }}</strong>
                        </div>
                    </div>

                    <section class="order-controls-card">
                        <div class="order-controls-heading">
                            <div>
                                <h3>Approval Controls</h3>
                                <p>Company-wide maker-checker policies for orders and accounting documents.</p>
                            </div>
                            <p-button *ngIf="isCompanyScope" type="button" label="Save Controls" icon="pi pi-save"
                                [loading]="orderControlsSaving" [disabled]="orderControlsLoading || orderControlsSaving"
                                (click)="saveOrderControls()"></p-button>
                        </div>
                        <div class="order-control-row">
                            <div>
                                <strong>Sales order maker-checker</strong>
                                <span>Prevent the creator or submitter from approving the same sales order.</span>
                            </div>
                            <p-toggleswitch [(ngModel)]="orderControlSettings.enforceSalesOrderMakerChecker"
                                [disabled]="!isCompanyScope || orderControlsLoading || orderControlsSaving"></p-toggleswitch>
                        </div>
                        <div class="order-control-row">
                            <div>
                                <strong>Purchase order maker-checker</strong>
                                <span>Prevent the creator or submitter from approving the same purchase order.</span>
                            </div>
                            <p-toggleswitch [(ngModel)]="orderControlSettings.enforcePurchaseOrderMakerChecker"
                                [disabled]="!isCompanyScope || orderControlsLoading || orderControlsSaving"></p-toggleswitch>
                        </div>
                        <div class="order-control-row">
                            <div>
                                <strong>Rental usage maker-checker</strong>
                                <span>Require a different user to approve rental usage after it has been submitted.</span>
                            </div>
                            <p-toggleswitch [(ngModel)]="orderControlSettings.enforceRentalUsageMakerChecker"
                                [disabled]="!isCompanyScope || orderControlsLoading || orderControlsSaving"></p-toggleswitch>
                        </div>
                        <div class="order-control-row" *ngFor="let control of accountingMakerCheckerControls">
                            <div>
                                <strong>{{ control.label }} maker-checker</strong>
                                <span>{{ control.description || 'Require a different user to approve a ' + control.label.toLowerCase() + ' after submission.' }}</span>
                            </div>
                            <p-toggleswitch [(ngModel)]="orderControlSettings[control.key]"
                                [disabled]="!isCompanyScope || orderControlsLoading || orderControlsSaving"></p-toggleswitch>
                        </div>
                    </section>

                    <section class="delivery-evidence-card">
                        <div class="delivery-evidence-heading">
                            <div>
                                <h3>Invoices</h3>
                                <p>Company-wide validation policy for bilingual invoice documents.</p>
                            </div>
                            <p-button *ngIf="isCompanyScope" type="button" label="Save Policy" icon="pi pi-save"
                                [loading]="singleInvoiceBilingualPolicySaving"
                                [disabled]="singleInvoiceBilingualPolicyLoading || singleInvoiceBilingualPolicySaving"
                                (click)="saveSingleInvoiceBilingualPolicy()"></p-button>
                        </div>
                        <div class="delivery-evidence-row">
                            <div>
                                <strong>Require bilingual invoices</strong>
                                <span>Block invoice generation until the required Arabic seller, buyer, and line-description data is complete.</span>
                            </div>
                            <p-toggleswitch [(ngModel)]="singleInvoiceBilingualPolicy.required"
                                [disabled]="!isCompanyScope || singleInvoiceBilingualPolicyLoading || singleInvoiceBilingualPolicySaving"></p-toggleswitch>
                        </div>
                    </section>

                    <section class="delivery-evidence-card">
                        <div class="delivery-evidence-heading">
                            <div>
                                <h3>Delivery Evidence</h3>
                                <p>Company-wide GPS stamping policy for driver delivery photos.</p>
                            </div>
                            <p-button *ngIf="isCompanyScope" type="button" label="Save Policy" icon="pi pi-save"
                                [loading]="deliveryEvidencePolicySaving"
                                [disabled]="deliveryEvidencePolicyLoading || deliveryEvidencePolicySaving"
                                (click)="saveDeliveryEvidencePolicy()"></p-button>
                        </div>
                        <div class="delivery-evidence-row">
                            <div>
                                <strong>Require GPS-stamped delivery evidence</strong>
                                <span>Stamp eligible driver photos with the resolved address, local capture time, coordinates, and GPS accuracy.</span>
                            </div>
                            <p-toggleswitch [(ngModel)]="deliveryEvidencePolicy.enabled"
                                [disabled]="!isCompanyScope || deliveryEvidencePolicyLoading || deliveryEvidencePolicySaving"></p-toggleswitch>
                        </div>
                        <div class="delivery-evidence-note">
                            Drivers need a fresh foreground location accurate to {{ deliveryEvidencePolicy.maxAccuracyMeters }} m or better. The visible stamp is an audit aid, not a cryptographic guarantee against a compromised device.
                        </div>
                    </section>
                </p-tabpanel>
            </p-tabpanels>
        </p-tabs>
    </app-page-data-panel>
</app-page-shell>

<app-company-form
    [displayModal]="showCompanyForm"
    [dialogHeader]="companyFormHeader"
    [submitButtonLabel]="companyFormSubmitLabel"
    [company]="selectedCompany"
    (modalClosed)="onCompanyFormClosed()">
</app-company-form>

<p-dialog
    header="Add Company File"
    [(visible)]="showCompanyFileDialog"
    [modal]="true"
    [closable]="!isCompanyFileUploadInProgress"
    [closeOnEscape]="!isCompanyFileUploadInProgress"
    [draggable]="false"
    [resizable]="false"
    [style]="{ width: 'min(760px, 94vw)' }"
    [breakpoints]="{ '700px': '96vw' }"
    styleClass="company-upload-dialog">
    <section class="company-upload-body">
        <aside class="company-upload-summary">
            <div class="company-upload-icon"><i class="pi pi-upload"></i></div>
            <div>
                <small>COMPANY WORKSPACE</small>
                <h3>Company files</h3>
                <p>Attach a registration document to {{ company?.name }}.</p>
            </div>
            <div class="company-upload-fact">
                <span>Selected file</span>
                <strong>{{ companyFileDraft.file?.name || 'No file selected' }}</strong>
            </div>
        </aside>
        <div class="company-upload-form">
            @if (companyFileUploadError) {
                <small class="company-upload-error" role="alert">{{ companyFileUploadError }}</small>
            }
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-tag"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <p-select inputId="companyFileCategory" [(ngModel)]="companyFileDraft.category"
                        [options]="companyFileTypes" optionLabel="label" optionValue="value"
                        [disabled]="isCompanyFileUploadInProgress" appendTo="body" styleClass="w-full"></p-select>
                    <label for="companyFileCategory">File Type</label>
                </p-iftalabel>
            </p-inputGroup>
            <div class="company-upload-file-field">
                <span>File</span>
                <div class="company-upload-file-picker">
                    <i class="pi pi-file"></i>
                    <div>
                        <strong>{{ companyFileDraft.file?.name || 'Choose a file' }}</strong>
                        <small>{{ companyFileDraft.file ? 'Ready to upload' : 'Select a document from your device' }}</small>
                    </div>
                    <label for="companyFileInput" [class.disabled]="isCompanyFileUploadInProgress">Browse</label>
                    <input id="companyFileInput" type="file" aria-label="Choose company file"
                        [disabled]="isCompanyFileUploadInProgress"
                        (change)="onCompanyFileSelected($event)" />
                </div>
            </div>
            @if (isCompanyFileUploadInProgress) {
                <small class="company-upload-status" role="status">{{ companyFileUploadStage === 'storage' ? 'Uploading file…' : 'Saving file details…' }}</small>
            }
        </div>
    </section>
    <ng-template pTemplate="footer">
        <div class="company-upload-footer">
            <span></span>
            <p-button type="button" label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true"
                [disabled]="isCompanyFileUploadInProgress" (click)="showCompanyFileDialog = false"></p-button>
            <p-button type="button" label="Upload" icon="pi pi-upload"
                [loading]="isCompanyFileUploadInProgress"
                [disabled]="!companyFileDraft.file || isCompanyFileUploadInProgress"
                (click)="uploadCompanyFile()"></p-button>
        </div>
    </ng-template>
</p-dialog>

<p-dialog header="Connect Bayan Naql" [(visible)]="showBayanDialog" [modal]="true" [style]="{ width: 'min(92vw, 520px)' }">
    <div class="company-file-form">
        <label>
            <span>Client ID</span>
            <input pInputText [(ngModel)]="bayanClientId" placeholder="Enter your Bayan client ID" />
            <small class="field-hint">The client ID provided by Bayan (Elm) for your company.</small>
        </label>
    </div>
    <ng-template pTemplate="footer">
        <p-button type="button" label="Cancel" severity="secondary" [text]="true" (click)="showBayanDialog = false"></p-button>
        <p-button type="button" label="Connect" icon="pi pi-link" [loading]="bayanLoading" [disabled]="!bayanClientId.trim()" (click)="submitBayanOnboarding()"></p-button>
    </ng-template>
</p-dialog>

<app-customer-form
    [displayModal]="showCustomerForm"
    [dialogHeader]="customerFormHeader"
    [submitButtonLabel]="customerFormSubmitLabel"
    [customer]="selectedCustomer"
    [company]="selectedCompany"
    [isEmployee]="true"
    (modalClosed)="onCustomerFormClosed()">
</app-customer-form>

<app-customer-roles
    [displayModal]="showCustomerRolesDialog"
    [dialogHeader]="'Manage Roles for ' + selectedCustomer.name"
    [customer]="selectedCustomer"
    (modalClosed)="onCustomerRolesDialogClosed()">
</app-customer-roles>
